import '../styles/contact.css';
import { contact } from '../content/copy';
import { site } from '../content/site';

type FormspreeError = {
  readonly error?: string;
  readonly errors?: readonly { readonly message?: string }[];
};

async function failureMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as FormspreeError;
    return body.errors?.[0]?.message ?? body.error ?? contact.errorMessage;
  } catch {
    return contact.errorMessage;
  }
}

export function mount(container: HTMLElement): () => void {
  const section = document.createElement('section');
  section.className = 'page page-contact';

  const socials = site.social
    .map(
      s =>
        `<li>
          <a
            class="social-link"
            href="${s.url}"
            title="${s.label}"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="social-icon" data-icon="${s.icon}" aria-hidden="true"></span>
            <span class="visually-hidden">${s.label}</span>
          </a>
        </li>`,
    )
    .join('');

  /*
   * `action`/`method` stay on the element so the form still posts natively —
   * and lands on Formspree's own thank-you page — if the module fails to load.
   * The submit handler below is the enhancement, not the mechanism.
   */
  section.innerHTML = `
    <div class="contact-content content-panel">
      <h2>${contact.heading}</h2>
      <ul class="social-links" role="list">${socials}</ul>
      <p class="socials-intro">${contact.body}</p>
      <form class="contact-form" action="${site.contactFormEndpoint}" method="POST">
        <label class="field">
          <span class="visually-hidden">${contact.emailLabel}</span>
          <input
            class="field-control"
            type="email"
            name="email"
            autocomplete="email"
            placeholder="${contact.emailLabel}"
            required
          />
        </label>
        <label class="field">
          <span class="visually-hidden">${contact.messageLabel}</span>
          <textarea
            class="field-control"
            name="message"
            rows="6"
            placeholder="${contact.messageLabel}"
            maxlength="250"
            required
          ></textarea>
        </label>
        <button class="submit-btn" type="submit">${contact.sendLabel}</button>
        <p class="form-status" role="status" aria-live="polite"></p>
      </form>
    </div>
  `;

  const form = section.querySelector('form')!;
  const button = section.querySelector<HTMLButtonElement>('.submit-btn')!;
  const status = section.querySelector<HTMLParagraphElement>('.form-status')!;
  const abort = new AbortController();

  function setStatus(state: 'pending' | 'ok' | 'error', message: string): void {
    status.dataset['state'] = state;
    status.textContent = message;
  }

  async function onSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (button.disabled) return;

    button.disabled = true;
    button.textContent = contact.sendingLabel;
    form.setAttribute('aria-busy', 'true');
    setStatus('pending', '');

    try {
      const res = await fetch(site.contactFormEndpoint, {
        method: 'POST',
        // No Content-Type — the browser has to write FormData's multipart boundary.
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        signal: abort.signal,
      });

      if (res.ok) {
        form.reset();
        setStatus('ok', contact.sentMessage);
      } else {
        setStatus('error', await failureMessage(res));
      }
    } catch (err) {
      // Unmounting aborts the request; the node is already detached.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setStatus('error', contact.errorMessage);
    } finally {
      if (!abort.signal.aborted) {
        button.disabled = false;
        button.textContent = contact.sendLabel;
        form.removeAttribute('aria-busy');
      }
    }
  }

  form.addEventListener('submit', event => void onSubmit(event));
  container.append(section);

  return () => {
    abort.abort();
    section.remove();
  };
}
